import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { 
  generateId, 
  addTimestamps, 
  validateIncome,
  INCOME_SCHEMA 
} from '../../../schemas/dataSchemas';
import { useUserData } from '../../../hooks/useUserData';

const IncomeSourceModal = ({ isOpen, onClose, onSave, editingSource = null }) => {
  const { userData } = useUserData();
  
  // Calculate max end year based on projection horizon
  const currentYear = new Date().getFullYear();
  const startYear = userData?.setup?.startYear || currentYear;
  const projectionHorizon = userData?.setup?.projectionHorizon || 20;
  const maxEndYear = startYear + projectionHorizon;
  const [formData, setFormData] = useState({
    type: 'salary',
    description: '',
    currentAmount: '',
    frequency: 'monthly',
    startYear: new Date().getFullYear(),
    endYear: new Date().getFullYear() + 25,
    growthRate: '3',
    probability: '90',
    isActive: true,
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingSource) {
      setFormData(editingSource);
    } else {
      setFormData({
        type: 'salary',
        description: '',
        currentAmount: '',
        frequency: 'monthly',
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear() + 25,
        growthRate: '3',
        probability: '90',
        isActive: true,
        notes: ''
      });
    }
    setErrors({});
  }, [editingSource, isOpen]);

  const incomeTypeOptions = [
    { value: 'salary', label: 'Salary Income' },
    { value: 'business', label: 'Business Income' },
    { value: 'rental', label: 'Rental Income' },
    { value: 'passive', label: 'Passive Income' },
    { value: 'investment', label: 'Investment Income' },
    { value: 'pension', label: 'Pension Income' },
    { value: 'other', label: 'Other Income' }
  ];

  const frequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' },
    { value: 'one-time', label: 'One-time' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check required fields first with user-friendly messages
    if (!formData.type || formData.type === '') {
      newErrors.type = 'Income type is required';
    }
    
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.currentAmount || formData.currentAmount === '' || parseFloat(formData.currentAmount) <= 0) {
      newErrors.currentAmount = 'Current amount is required and must be greater than 0';
    }
    
    if (!formData.frequency || formData.frequency === '') {
      newErrors.frequency = 'Frequency is required';
    }
    
    const startYearValue = parseInt(formData.startYear);
    const endYearValue = parseInt(formData.endYear);
    const minYear = currentYear - 1;
    
    if (!formData.startYear || isNaN(startYearValue) || startYearValue < minYear || startYearValue > maxEndYear) {
      newErrors.startYear = `Start year is required and must be between ${minYear} and ${maxEndYear}`;
    }
    
    if (!formData.endYear || isNaN(endYearValue) || endYearValue < minYear || endYearValue > maxEndYear) {
      newErrors.endYear = `End year is required and must be between ${minYear} and ${maxEndYear}`;
    }
    
    if (startYearValue && endYearValue && startYearValue > endYearValue) {
      if (!newErrors.startYear) newErrors.startYear = 'Start year must be before end year';
      if (!newErrors.endYear) newErrors.endYear = 'End year must be after start year';
    }
    
    // Validate amount is a valid number
    const amount = parseFloat(formData.currentAmount);
    if (formData.currentAmount && (isNaN(amount) || amount <= 0)) {
      newErrors.currentAmount = 'Current amount must be a valid positive number';
    }
    
    // Validate growth rate
    if (!formData.growthRate || formData.growthRate === '') {
      newErrors.growthRate = 'Annual growth rate is required';
    } else {
      const growthRate = parseFloat(formData.growthRate);
      if (isNaN(growthRate) || growthRate < 0 || growthRate > 50) {
        newErrors.growthRate = 'Growth rate must be between 0% and 50%';
      }
    }
    
    // Validate probability
    if (!formData.probability || formData.probability === '') {
      newErrors.probability = 'Probability is required';
    } else {
      const probability = parseFloat(formData.probability);
      if (isNaN(probability) || probability < 0 || probability > 100) {
        newErrors.probability = 'Probability must be between 0% and 100%';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    // Final validation using schema validator
    const validationData = {
      id: editingSource?.id || generateId(),
      type: formData.type,
      description: formData.description.trim(),
      currentAmount: parseFloat(formData.currentAmount),
      frequency: formData.frequency,
      startYear: parseInt(formData.startYear),
      endYear: parseInt(formData.endYear),
      growthRate: parseFloat(formData.growthRate),
      probability: parseFloat(formData.probability),
      isActive: formData.isActive,
      notes: formData.notes
    };

    const validation = validateIncome(validationData);
    
    if (!validation.isValid) {
      // Merge any additional validation errors
      validation.errors.forEach(error => {
        if (error.includes('description') && !newErrors.description) newErrors.description = error;
        if ((error.includes('currentAmount') || error.includes('amount')) && !newErrors.currentAmount) newErrors.currentAmount = error;
        if (error.includes('type') && !newErrors.type) newErrors.type = error;
        if (error.includes('frequency') && !newErrors.frequency) newErrors.frequency = error;
        if (error.includes('startYear') && !newErrors.startYear) newErrors.startYear = error;
        if (error.includes('endYear') && !newErrors.endYear) newErrors.endYear = error;
        if (error.includes('growthRate') && !newErrors.growthRate) newErrors.growthRate = error;
        if (error.includes('probability') && !newErrors.probability) newErrors.probability = error;
      });
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const processedData = {
        id: editingSource?.id || generateId(),
        type: formData.type,
        description: formData.description.trim(),
        currentAmount: parseFloat(formData.currentAmount),
        frequency: formData.frequency,
        startYear: parseInt(formData.startYear),
        endYear: parseInt(formData.endYear),
        growthRate: parseFloat(formData.growthRate),
        probability: parseFloat(formData.probability),
        isActive: formData.isActive,
        notes: formData.notes.trim()
      };
      
      // Add timestamps using schema utility
      const finalData = addTimestamps(processedData, !editingSource);
      
      onSave(finalData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-floating w-full max-w-2xl min-h-fit my-4 mx-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {editingSource ? 'Edit Income Source' : 'Add Income Source'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-destructive mb-1">
                    Please fix the following errors:
                  </h3>
                  <ul className="text-sm text-destructive space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field} className="flex items-start gap-2">
                        <span className="text-destructive">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Income Type"
              options={incomeTypeOptions}
              value={formData.type}
              onChange={(value) => handleInputChange('type', value)}
              error={errors.type}
              required
            />

            <Input
              label="Description"
              type="text"
              placeholder="e.g., Primary Salary, Rental Property A"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              error={errors.description}
              required
            />

            <Input
              label="Current Amount (₹)"
              type="number"
              placeholder="50000"
              value={formData.currentAmount}
              onChange={(e) => handleInputChange('currentAmount', e.target.value)}
              error={errors.currentAmount}
              required
              description="Enter amount in Indian Rupees"
            />

            <Select
              label="Frequency"
              options={frequencyOptions}
              value={formData.frequency}
              onChange={(value) => handleInputChange('frequency', value)}
              error={errors.frequency}
              required
            />

            <Input
              label="Start Year"
              type="number"
              min={currentYear - 1}
              max={maxEndYear}
              value={formData.startYear}
              onChange={(e) => handleInputChange('startYear', e.target.value)}
              error={errors.startYear}
              required
            />

            <Input
              label="End Year"
              type="number"
              min={currentYear - 1}
              max={maxEndYear}
              value={formData.endYear}
              onChange={(e) => handleInputChange('endYear', e.target.value)}
              error={errors.endYear}
              required
            />

            <Input
              label="Annual Growth Rate (%)"
              type="number"
              step="0.1"
              min="0"
              max="50"
              placeholder="3.0"
              value={formData.growthRate}
              onChange={(e) => handleInputChange('growthRate', e.target.value)}
              error={errors.growthRate}
              description="Expected annual increase percentage"
              required
            />

            <Input
              label="Probability (%)"
              type="number"
              min="0"
              max="100"
              placeholder="90"
              value={formData.probability}
              onChange={(e) => handleInputChange('probability', e.target.value)}
              error={errors.probability}
              description="Confidence level for this income stream"
              required
            />
          </div>

          <Input
            label="Notes (Optional)"
            type="text"
            placeholder="Additional details about this income source"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
          />

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
            >
              {editingSource ? 'Update Income Source' : 'Add Income Source'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncomeSourceModal;